import { useRef, useState, useEffect } from "react";
import useAuth from "../hooks/useAuth";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "../api/axios";
import { endpoints } from "../utils/utils";
import { useGoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import AppleLogin from "react-apple-login";
import useApi from "../hooks/useApi";
import { useNotifications } from '../context/NotificationProvider';
import AuthService from "../services/auth.service";
import UserService from "../services/user.service";
import { syncWebToken } from "../utils/webPushSync";
import { withSafeUserExtra } from "../utils/userDisplay";
import FacebookLogin from '@greatsumini/react-facebook-login';
import LanguageSwitcher from "./LanguageSwitcher";
import { Box, Button, createTheme, ThemeProvider } from "@mui/material";
import { useTranslation } from 'react-i18next';
import fb from '../assets/images/facebook.svg';
import { Visibility, VisibilityOff } from "@mui/icons-material";

const theme = createTheme({
  palette: {
    white: {
      "main": "#FFFFFF",
      "light": "#FFFFFF",
      "dark": "#FFFFFF",
      "contrastText": "#FFFFFF"
    },
    dark_yellow: {
      "main": "#393D44",
      "light": "#393D44",
      "dark": "#393D44",
      "contrastText": "#FAEF9B",
    },
    dark_white: {
      "main": "#393D44",
      "light": "#393D44",
      "dark": "#393D44",
      "contrastText": "#FFFFFF",
    },
    dark: {
      "main": "#393D44",
      "light": "#393D44",
      "dark": "#393D44",
      "contrastText": "#393D44"
    }
  },
});

const Login = () => {
  const { t, i18n } = useTranslation('auth');
  // eslint-disable-next-line no-unused-vars
  const { setAuth, persist, setPersist } = useAuth();

  const navigate = useNavigate();
  const api = useApi();
  const [searchParams] = useSearchParams();
  const notify = useNotifications();
  const emailVerificationStatus = searchParams.get('email_verification_status');

  const userRef = useRef();
  const [user, setUser] = useState("");
  const [pwd, setPwd] = useState("");
  const [errMsg, setErrMsg] = useState("");

  const isProfileCompletionRequired = (error) =>
    error?.response?.data?.code === "PROFILE_COMPLETION_REQUIRED";

  const routeToSocialRegistration = ({
    provider,
    token,
    responseData,
    firstname,
    lastname,
  }) => {
    if (!token) return;

    navigate("/register", {
      state: {
        socialCompletion: true,
        provider,
        token,
        email: responseData?.email,
        firstname,
        lastname,
      },
    });
  };

  const handleFacebookCallback = async (response) => {
    if (response?.status === "unknown") {
      console.error('Sorry!', 'Something went wrong with facebook Login.');
      return;
    }
    try {
      const token = response.accessToken;
      const res = await AuthService.socialLogin(
        { token, appName: "MyCoolApp" },
        api,
        "facebook"
      )
      afterLogin(res)
    } catch (error) {
      if (isProfileCompletionRequired(error)) {
        routeToSocialRegistration({
          provider: "facebook",
          token: response.accessToken,
          responseData: error.response?.data,
        });
      }
    }

  }

  const appleResponse = async (response) => {
    if (!response.error) {
      const token = response?.authorization?.id_token;
      const firstname = response?.user?.name?.firstName;
      const lastname = response?.user?.name?.lastName;

      if (!token) return;

      try {
        const res = await AuthService.socialLogin(
          {
            token,
            ...(firstname ? { firstname, given_name: firstname } : {}),
            ...(lastname ? { lastname, family_name: lastname } : {}),
          },
          api,
          "apple"
        )
        afterLogin(res)
      } catch (error) {
        if (isProfileCompletionRequired(error)) {
          routeToSocialRegistration({
            provider: "apple",
            token,
            responseData: error.response?.data,
            firstname,
            lastname,
          });
        }
      }

    }
  };

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };


  const login = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      try {
        const token = codeResponse.access_token;
        const response = await AuthService.socialLogin(
          { token },
          api,
          "google"
        )
        afterLogin(response)
      } catch (error) {
        if (isProfileCompletionRequired(error)) {
          routeToSocialRegistration({
            provider: "google",
            token: codeResponse.access_token,
            responseData: error.response?.data,
          });
        }
      }
    },
    onError: (error) => console.log("Login Failed:", error),
  });
  // useEffect(() => {
  //   if (!localStorage.getItem('i18nextLng')) {
  //     i18n.changeLanguage('fr');
  //   }
  // }, [i18n]);

  useEffect(() => {
    userRef.current.focus();
  }, []);

  useEffect(() => {
    setErrMsg("");
  }, [user, pwd]);

  const afterLogin = (response) => {
    console.log("response login", response)
    if (response?.user?.status === 'ACTIVE' || response?.user?.status === 'PENDING_PHONE_VERIFICATION') {
      const user = withSafeUserExtra(response?.user);
      const accessToken = response?.token;
      const roles = user?.roles;
      const status = user?.status;
      const refresh = response?.refresh_token;
      const active = user?.status === 'ACTIVE' || user?.status === 'PENDING_PHONE_VERIFICATION';
      localStorage.setItem("accessToken", response?.token);
      console.log(accessToken);
      setAuth({ user, pwd, roles, accessToken, refresh, active, status });
      setUser("");
      setPwd("");
      /* const redirect = roles.filter((element) => {
        return element === "ROLE_CLIENT";
      }); */
      const redirectAfterLogin = sessionStorage.getItem('redirectAfterLogin')
      const storedState = sessionStorage.getItem('state');
      if (user?.status === 'PENDING_PHONE_VERIFICATION') {
        navigate("/VerificationNumero", { replace: true });
      } else if (user?.should_reset_password) {
        navigate("/reset-password", { state: { token: response?.token } });
        return;
      }
      else if (redirectAfterLogin) {
        const state = storedState ? JSON.parse(storedState) : null;
        navigate(redirectAfterLogin, { state });  // Pass the state to the redirected page
        sessionStorage.removeItem('redirectAfterLogin');
        sessionStorage.removeItem('state');
      }
      else {
        if (user?.id) {
          syncWebToken({
            userId: user.id,
            appName: "client",
            axios: api,
          }).catch(() => {});
        }

        navigate("/", { replace: true });
        console.log(response);
      }
    } else setErrMsg("Votre compte n'est pas actif");
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    /*  console.log("params", params.id); */

    try {
      const response = await AuthService.login({ email: user, password: pwd }, api);
      console.log("data", response)
      afterLogin(response)

    } catch (err) {
      //errRef.current.focus();

    }
  };



  return (
    <div className="main-login">
      <div className="container">
        <div className="admin-left">
          <div className="logo">Flyspa</div>
          <h2>{t('login.title')}</h2>
          <form name="" id="" onSubmit={handleSubmit}>
            {errMsg ? <p className="error">{errMsg}</p> : ""}
            <div className="form-text">
              <input
                type="email"
                name="email"
                id="email"
                placeholder={t('login.email')}
                className="email"
                ref={userRef}
                autoComplete="off"
                onChange={(e) => setUser(e.target.value)}
                value={user}
                required
              />
            </div>
            <div className="form-text password-container">
              <input
                type={showPassword ? "text" : "password"}
                onChange={(e) => setPwd(e.target.value)}
                name="password"
                id="password"
                value={pwd}
                placeholder={t('login.password')}
                className="password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <Visibility /> : <VisibilityOff />}
              </button>
            </div>
            <Link to="/forget_password" className="pass-oublie">
              {t('login.forgotPassword')}
            </Link>
            <div className="form-submit">
              {/* <input type="submit" name="sign" id="sign" value={t('login.signIn')} /> */}
              <Button
                type="submit"
                variant="contained"
                name="sign"
                id="sign"
                fullWidth
              >
                <img src="assets/images/icon-login.svg" />
                {t('login.signIn')}
              </Button>
            </div>
          </form>
          <div className="form-login">
            <div className="text-ou">{t('login.or')}</div>
            <div className="sign-apple-google">
              <AppleLogin
                clientId="com.flyspa.web"
                redirectURI={process.env.REACT_APP_APPLE_REDIRECT}
                usePopup={true}
                callback={appleResponse} // Catch the response
                scope="email name"
                responseMode="query"
                render={(
                  renderProps //Custom Apple Sign in Button
                ) => (
                  <button className="login-with-apple-btn"
                    onClick={renderProps.onClick}
                  >
                    {t('login.withApple')}
                  </button>
                )}
              />

              <button className="login-with-google-btn" onClick={() => login()}>
                {t('login.withGoogle')}
              </button>
              <FacebookLogin
                appId="2082697205875306"
                language="fr_FR"
                callback={(response) => {
                  const appName = "MyCoolApp"; // <-- manually
                  console.log('Login Success!', { ...response, appName });
                  handleFacebookCallback({ ...response, appName });
                }}
                onFailure={(error) => {
                  console.log('Login Failed!', error);
                }}
                render={renderProps => (
                  <button
                    onClick={renderProps.onClick}
                    className="login-with-fc-btn"
                    style={{
                      backgroundColor: '#4267b2',
                      color: '#fff',
                      fontSize: '16px',
                      padding: '12px 24px',
                      border: 'none',
                      borderRadius: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <img className="https-app" src={fb} alt="Edit" style={{ height: '24px' }} />
                    {t('login.withFacebook')}
                  </button>
                )}
              />

            </div>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <div className="inscription">
                {t('login.noAccount')}<br /><br />
                <Link to="/register">{t('login.register')}</Link>
              </div>
              <ThemeProvider theme={theme}>
                <Button variant="outlined" color='white' type="button"
                  onClick={() => navigate('/')} sx={{ textTransform: 'none', borderRadius: '15px', width: '100%', maxWidth: '220px', minHeight: '57px' }}>{t('login.skip')}</Button>
              </ThemeProvider>
              <LanguageSwitcher />
            </Box>

          </div>
        </div>
      </div >
    </div >
  );
};

export default Login;
